import heic2any from 'heic2any'

export class ImagesService {
    /**
     * Converte uma imagem HEIC em base64 para JPG em base64
     * @param heicBase64 - String base64 da imagem HEIC
     * @returns Promise<string> - String base64 da imagem JPG
     */
    async convertHeicBase64ToJpg(heicBase64: string): Promise<string> {
        // Converter base64 para Blob
        const heicBlob = this.base64ToBlob(heicBase64, 'image/heic')

        // Converter HEIC para JPG
        const jpgBlob = (await heic2any({
            blob: heicBlob,
            toType: 'image/jpeg',
            quality: 0.8,
        })) as Blob

        // Converter Blob para base64
        return this.blobToBase64(jpgBlob)
    }

    /**
     * Reduz uma imagem base64 para caber em maxWidth x maxHeight, mantendo a proporção.
     *
     * A escala é a menor entre `maxWidth / largura` e `maxHeight / altura` — assim a imagem cabe
     * nos dois limites e nenhum lado é distorcido. Imagem que já cabe é devolvida intacta, sem
     * recodificar (recodificar de graça só perderia qualidade e tempo).
     *
     * Existe porque o que é enviado é o que fica: o navegador decodifica a imagem no tamanho
     * original (largura x altura x 4 bytes), independente do tamanho em que ela é exibida. Um
     * arquivo de 15000x15000 ocupa ~858 MB descomprimido e derrubou a geração de PDF por falta de
     * memória, mesmo aparecendo num logo de 86px.
     */
    async resizeBase64(base64: string, maxWidth: number, maxHeight: number, quality = 0.85): Promise<string> {
        if (!base64 || !maxWidth || !maxHeight) return base64
        const mimeType = base64.match(/^data:([^;]+);/)?.[1] ?? 'image/jpeg'
        // SVG é vetor: passar por canvas rasterizaria (arquivo maior e serrilhado ao ampliar).
        // GIF perderia a animação, porque o canvas só captura o primeiro quadro.
        if (mimeType === 'image/svg+xml' || mimeType === 'image/gif') return base64
        let bitmap: ImageBitmap
        try {
            bitmap = await createImageBitmap(this.base64ToBlob(base64, mimeType))
        } catch (error) {
            // Formato que o navegador não decodifica (ou base64 corrompido): devolve como veio em
            // vez de impedir o upload — reduzir é otimização, não requisito.
            return base64
        }
        try {
            const scale = Math.min(maxWidth / bitmap.width, maxHeight / bitmap.height, 1)
            if (scale === 1) return base64

            const canvas = document.createElement('canvas')
            canvas.width = Math.max(1, Math.round(bitmap.width * scale))
            canvas.height = Math.max(1, Math.round(bitmap.height * scale))
            const context = canvas.getContext('2d')
            if (!context) return base64
            context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)

            // PNG preserva transparência (importante para logo), mas é péssimo para foto: mantém o
            // formato só quando ele pode estar carregando canal alpha.
            const outputType = mimeType === 'image/png' ? 'image/png' : 'image/jpeg'
            return canvas.toDataURL(outputType, quality)
        } finally {
            // Libera o bitmap decodificado na hora, sem esperar o coletor de lixo — com imagem
            // grande a diferença é de centenas de MB.
            bitmap.close()
        }
    }

    /**
     * Converte uma string base64 para Blob
     */
    private base64ToBlob(base64: string, mimeType: string): Blob {
        const byteCharacters = atob(base64.split(',')[1])
        const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i))
        const byteArray = new Uint8Array(byteNumbers)
        return new Blob([byteArray], { type: mimeType })
    }

    /**
     * Converte um Blob para base64
     */
    private blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.readAsDataURL(blob)
        })
    }
}
